import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Candidate } from "@/types/candidate";
import { checkCVExists } from "@/utils/candidate/cv-storage";

// Define a type that explicitly includes ai_synthesis
interface SupabaseCandidate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  function?: string;
  skills?: string[];
  match_score?: number;
  created_at?: string;
  updated_at?: string;
  manager?: string;
  status?: string;
  unavailable_until?: string;
  ai_analysis?: any;
  ai_science_analysis?: any;
  ai_synthesis?: string;
  embedding?: any;
  linkedin?: string; // Add LinkedIn field
  location?: string; // Add location field
}

export const useCandidateData = (id: string | undefined): {
  loading: boolean;
  candidate: Candidate | null;
  cv: string | null;
  cvExists: boolean;
  refetch: () => Promise<Candidate | null>;
} => {
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [cv, setCv] = useState<string | null>(null);
  const [cvExists, setCvExists] = useState(false);

  const fetchCandidateData = useCallback(async (): Promise<Candidate | null> => {

    if (!id) return null;


    try {
      //console.log("Fetching candidate data for ID:", id);
      // Fetch candidate data from Supabase
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error("Error fetching candidate:", error);
        throw error;
      }

      // Cast data to our helper type to handle ai_synthesis
      const candidateData = data as SupabaseCandidate;

      // Make sure ai_analysis is of the correct type
      const aiAnalysis = candidateData.ai_analysis ?
        (typeof candidateData.ai_analysis === 'object' ? candidateData.ai_analysis : {}) :
        undefined;

      // Make sure ai_science_analysis is of the correct type
      const aiScienceAnalysis = candidateData.ai_science_analysis ?
        (typeof candidateData.ai_science_analysis === 'object' ? candidateData.ai_science_analysis : {}) :
        undefined;

      // Convert the database data to match our Candidate type
      const candidate: Candidate = {
        id: candidateData.id,
        name: candidateData.name,
        email: candidateData.email,
        phone: candidateData.phone || undefined,
        function: candidateData.function || undefined,
        skills: candidateData.skills || [],
        match_score: candidateData.match_score || undefined,
        created_at: candidateData.created_at || undefined,
        updated_at: candidateData.updated_at || undefined,
        manager: candidateData.manager || undefined,
        status: candidateData.status || undefined,
        unavailable_until: candidateData.unavailable_until || undefined,
        ai_analysis: aiAnalysis,
        ai_science_analysis: aiScienceAnalysis,
        ai_synthesis: candidateData.ai_synthesis || undefined,
        embedding: candidateData.embedding || undefined,
        linkedin: candidateData.linkedin || undefined, // Include LinkedIn
        location: candidateData.location || undefined, // Include location
        custom_fields: (candidateData as any).custom_fields || {},
      };

      //console.log("Processed candidate with AI synthesis:", candidate.ai_synthesis);
      setCandidate(candidate);

      // Check if candidate has a CV
      try {
        const { data: resumeList, error: errorFetchResume } = await supabase.functions.invoke("get-candidate-resume", {
          body: {
            candidateId: id
          }
        });

        if (errorFetchResume) {
          // Handle 404 errors gracefully (no CV exists)
          if (errorFetchResume.message?.includes('404') || errorFetchResume.message?.includes('Not Found')) {
            setCvExists(false);
            setCv(null);
            return candidate;
          }
          // For other errors, log but don't throw
          console.warn("Error fetching resume list:", errorFetchResume);
          setCvExists(false);
          setCv(null);
          return candidate;
        }

        if (resumeList === null) {
          setCvExists(false);
          setCv(null);
          return candidate;
        }

        if (resumeList.error) {
          console.error("Error fetching resume list:", resumeList.error);
          setCvExists(false);
          setCv(null);
          return candidate;
        }

        //console.log("Resume list fetched:", resumeList.resume.currentResume.publicUrl);

        setCvExists(true);
        setCv(resumeList.resume.currentResume.publicUrl);

        return candidate;
      } catch (error) {
        // Handle any other errors gracefully
        console.warn("Error checking CV existence:", error);
        setCvExists(false);
        setCv(null);
        return candidate;
      } finally {
        setLoading(false);
      }

    } catch (error) {
      console.error("Error in fetchCandidateData:", error);
      toast({
        title: "Error loading candidate",
        description: "Could not load candidate details",
        variant: "destructive",
      });
      setLoading(false);
      return null;
    }
  }, [id]);

  useEffect(() => {
    fetchCandidateData();
  }, [fetchCandidateData]);

  return {
    loading,
    candidate,
    cv,
    cvExists,
    refetch: fetchCandidateData
  };
};
